/**
 * Fetches data from the API with authentication.
 * Handles GET by default, but can accept options for other methods (POST, PATCH, etc.).
 *
 * @param {string} token - The Auth0 access token.
 * @param {string} endpoint - The API endpoint path (e.g., '/backtest/stats').
 * @param {object} [options={}] - Optional fetch options (method, body, headers).
 * @returns {Promise<object>} - The JSON response data.
 * @throws {Error} - Throws an error if the fetch fails or returns a non-OK status.
 */
import { emit } from './eventBus';
import getApiBaseUrl from './apiBaseUrl';
import fetchWithTimeout from './fetchWithTimeout';

export const DEFAULT_AUTH_FETCH_TIMEOUT_MS = 15000;

const isAuthFetchDebugEnabled = () => {
  try {
    return (
      typeof window !== 'undefined' &&
      (window.localStorage?.getItem('apiDebug') === '1' ||
        window.localStorage?.getItem('aiAgentDebug') === '1')
    );
  } catch {
    return false;
  }
};

const logAuthFetchDebug = (...args) => {
  if (isAuthFetchDebugEnabled()) {
    console.debug(...args);
  }
};

const logAuthFetchWarning = (...args) => {
  if (isAuthFetchDebugEnabled()) {
    console.warn(...args);
  }
};

const logAuthFetchError = (...args) => {
  if (isAuthFetchDebugEnabled()) {
    console.error(...args);
  }
};

export const fetchWithAuth = async (token, endpoint, options = {}) => {
  // Hosted bundles must never fall back to the React origin when an injected
  // API value is missing or accidentally points at the frontend host.
  const url = `${getApiBaseUrl()}${endpoint}`;
  const method = options.method || 'GET';
  const { timeoutMs = DEFAULT_AUTH_FETCH_TIMEOUT_MS, ...requestOptions } = options;
  logAuthFetchDebug(`[fetchWithAuth] ${method} ${url}`);

  const isFormDataBody = typeof FormData !== 'undefined' && requestOptions.body instanceof FormData;
  const defaultHeaders = {
    Authorization: `Bearer ${token}`,
    ...(!isFormDataBody ? { 'Content-Type': 'application/json' } : {}),
  };

  const mergedHeaders = {
    ...defaultHeaders,
    ...(requestOptions.headers || {}),
  };

  const fetchOptions = {
    ...requestOptions,
    headers: mergedHeaders,
  };

  if (fetchOptions.body && typeof fetchOptions.body === 'object' && !isFormDataBody) {
    fetchOptions.body = JSON.stringify(fetchOptions.body);
    // SECURITY: Never log request bodies as they may contain sensitive credentials
  }

  try {
    logAuthFetchDebug(`[fetchWithAuth] Awaiting fetch for ${method} ${url}...`);
    const response = await fetchWithTimeout(url, fetchOptions, timeoutMs);
    logAuthFetchDebug(
      `[fetchWithAuth] Fetch completed for ${method} ${url}. Status: ${response.status}`,
    );

    let responseData = {};
    let responseText = ''; // Store raw text response

    try {
      // Try reading response as text first to avoid issues with empty/non-JSON bodies
      responseText = await response.text();

      // Attempt to parse as JSON only if there's content
      if (responseText) {
        responseData = JSON.parse(responseText);
        // SECURITY: Don't log response data as it may contain sensitive information
      } else {
        // Response body is empty
        responseData = {}; // Treat empty response as empty object
      }
    } catch (jsonError) {
      logAuthFetchWarning(
        `[fetchWithAuth] Failed to parse JSON response (Status ${response.status}). Error: ${jsonError.message}.`,
      );
      if (response.ok) {
        // An HTTP status is not domain data. A fabricated success payload can
        // overwrite deployment state and bypass callers' existing retry paths.
        const error = new Error('Invalid JSON response');
        error.status = response.status;
        throw error;
      }
      // If JSON parsing failed AND response was not ok, prepare to throw error with text
      responseData = { error: `Invalid JSON response: ${responseText}` }; // Use text in error
    }

    if (!response.ok) {
      // Emit a unified auth error signal on 401 to trigger reauth UX globally
      if (response.status === 401) {
        try {
          emit('auth:reauth_required', {
            endpoint,
            method,
            status: response.status,
            at: Date.now(),
          });
        } catch (_) {
          // Swallow bus errors; we still throw below
        }
      }
      const errorMessage =
        responseData?.message ||
        responseData?.error ||
        responseText ||
        `HTTP error! status: ${response.status}`;
      logAuthFetchError(
        `[fetchWithAuth] Error Response (Status ${response.status}): ${errorMessage}`,
        responseData,
      );
      const error = new Error(errorMessage);
      // Attach the full parsed data (which might contain errorCode, limit, etc.)
      error.data = responseData;
      // Optionally attach status code directly as well
      error.status = response.status;
      if (response.status === 401) {
        error.isAuthError = true;
      }
      throw error; // Throw the augmented error object
    }

    // If response is OK and JSON parsing worked (or body was empty), return data
    return responseData;
  } catch (error) {
    // This catches network errors or errors thrown from the !response.ok check
    logAuthFetchError(`[fetchWithAuth] Network or processing error for ${method} ${url}:`, error);
    // Ensure the error object has a message property
    const finalError = error instanceof Error ? error : new Error(String(error));
    // Ensure 'data' property exists if error didn't originate from the !response.ok block
    const timedOutOrAborted =
      finalError.name === 'AbortError' || finalError.name === 'TimeoutError';
    if (!finalError.data) {
      finalError.data = { message: timedOutOrAborted ? 'Request timed out' : finalError.message }; // Add a minimal data object
    }
    // Add status if missing
    if (!finalError.status) {
      finalError.status = timedOutOrAborted ? 408 : error.status || 500; // Default to 500 if status unknown
    }
    // If we receive a 401 at this layer (e.g., from network layer), emit reauth event too
    // IMPORTANT: Only emit if we haven't already emitted (check isAuthError flag)
    if (finalError.status === 401 && !finalError.isAuthError) {
      try {
        emit('auth:reauth_required', {
          endpoint,
          method,
          status: 401,
          at: Date.now(),
        });
      } catch (_) {}
      finalError.isAuthError = true;
    }
    throw finalError; // Re-throw the error (now potentially augmented)
  }
};
